import { FC, useMemo } from 'react';
import { ResponsiveFunnel } from '@nivo/funnel';
import type { FunnelDatum } from '@nivo/funnel';
import { ListFilter } from 'lucide-react';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useNivoChartTheme } from '@/hooks/useNivoChartTheme';
import { EmptySection } from '@/shared';
import { evaluateComponentReadiness } from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/evaluateComponentReadiness';
import {
    useReportData,
    useReportQuery,
} from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/hook';
import { chartTransformers } from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/transformers';
import { ReportComponent, ReportComponentType } from '@/types';
import { ReportComponentConfig } from '@/types/api/modules/business-intelligence';

import { filterZeroValueItems, getChartFormatting, humanizeChartLabel, truncateChartLabel } from '../chartPresentation';
import CustomNivoChartTooltip from '../CustomNivoChartTooltip';
import { useNivoReportFormatting } from '../useNivoReportFormatting';

interface FunnelChartProps {
    component: ReportComponent;
    numerator: number;
    denominator: number;
}

type FunnelChartDatum = FunnelDatum<Record<string, number | string>>;

const fallbackData: FunnelChartDatum[] = [
    { id: 'step_sent', value: 68550, label: 'Sent' },
    { id: 'step_viewed', value: 57280, label: 'Viewed' },
    { id: 'step_clicked', value: 54412, label: 'Clicked' },
    { id: 'step_add_to_card', value: 43885, label: 'Add To Cart' },
    { id: 'step_purchased', value: 33555, label: 'Purchased' },
];

const isFunnelDatum = (value: unknown): value is FunnelChartDatum => {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const datum = value as Record<string, unknown>;
    const hasId = typeof datum.id === 'string' || typeof datum.id === 'number';
    const numericValue = Number(datum.value);

    return hasId && Number.isFinite(numericValue);
};

const FunnelChart: FC<FunnelChartProps> = ({ component, numerator, denominator }) => {
    const { token } = useAntDesignTheme();
    const { nivoChartTheme, colorsArray } = useNivoChartTheme();
    const { formatMetricValue, getMetricLabel } = useNivoReportFormatting(component);

    const config = component.configuration as ReportComponentConfig;
    const layout = config?.formatting?.layout ?? 'vertical';
    const formatting = getChartFormatting(config);
    const { isReady } = evaluateComponentReadiness(component);

    const query = useReportQuery(component);
    const { data: rawData } = useReportData(component);
    const transformer = chartTransformers[component.type as ReportComponentType];

    const realData = useMemo<FunnelChartDatum[]>(() => {
        const transformed = transformer?.(rawData, query);

        if (!Array.isArray(transformed)) {
            return [];
        }

        return transformed.filter(isFunnelDatum).map<FunnelChartDatum>((item) => ({
            id: item.id,
            label: item.label ?? String(item.id),
            value: Number(item.value),
        }));
    }, [rawData, query, transformer]);

    const presentedRealData = filterZeroValueItems(realData, formatting.showZeroValues);
    const data = (isReady ? presentedRealData : fallbackData).map((item) => ({
        ...item,
        label: truncateChartLabel(item.label ?? item.id, 18),
    }));

    if (isReady && !data.length) {
        return (
            <EmptySection
                height='100%'
                icon={<ListFilter size='70%' style={{ color: token.colorTextTertiary }} />}
                label='No Funnel Data'
                description='The selected dataset and fields did not return any values.'
            />
        );
    }

    return (
        <div
            style={{
                aspectRatio: `${numerator}/${denominator}`,
                height: '100%',
                minHeight: 0,
                width: '100%',
            }}
        >
            <ResponsiveFunnel
                data={data}
                direction={layout}
                margin={{ top: 24, right: 24, bottom: 24, left: 24 }}
                valueFormat={(value) => formatMetricValue(value)}
                tooltip={({ part }) => (
                    <CustomNivoChartTooltip
                        title={humanizeChartLabel(part.data.label ?? part.data.id)}
                        entries={[
                            {
                                color: part.color,
                                label: getMetricLabel() || 'Value',
                                value: part.data.value,
                            },
                        ]}
                        formatValue={formatMetricValue}
                    />
                )}
                borderWidth={8}
                borderOpacity={0.25}
                fillOpacity={1}
                labelColor={token.colorText}
                colors={colorsArray}
                beforeSeparatorLength={layout === 'vertical' ? 128 : 48}
                afterSeparatorLength={layout === 'vertical' ? 128 : 48}
                currentPartSizeExtension={8}
                theme={nivoChartTheme}
                shapeBlending={0.75}
                motionConfig='gentle'
            />
        </div>
    );
};

export default FunnelChart;
